const finiteValues = values => values.map(Number).filter(Number.isFinite)

const summarizeChannel = (points, key, label, unit) => {
  const values = finiteValues(points.map(point => point[key]))
  if (!values.length) return null
  return {
    key,
    label,
    unit,
    minimum: Math.min(...values),
    maximum: Math.max(...values),
    average: values.reduce((sum, value) => sum + value, 0) / values.length,
    sampleCount: values.length,
  }
}

const iracingChannelDefinitions = [
  ['speedKmh', 'Speed', 'km/h'],
  ['throttlePct', 'Throttle', '%'],
  ['brakePct', 'Brake', '%'],
  ['steeringDeg', 'Steering', 'deg'],
  ['rpm', 'Engine speed', 'rpm'],
  ['gear', 'Gear', ''],
  ['lateralG', 'Lateral acceleration', 'g'],
  ['longitudinalG', 'Longitudinal acceleration', 'g'],
]

const normalizeIRacingSampleAnalytics = report => {
  const points = (report.traces || []).flatMap(trace => trace.points || [])
  const comparisonLaps = report.analysisLaps?.comparisonLaps || []
  return {
    source: 'iracing',
    referenceLap: report.analysisLaps?.referenceLap,
    comparisonLaps,
    laps: (report.laps || []).map(lap => ({
      number: lap.number,
      timeSeconds: Number(lap.timeS),
      label: lap.formattedTime,
      status: lap.isClean ? 'Clean' : 'Incident',
    })),
    channels: iracingChannelDefinitions
      .map(([key, label, unit]) => summarizeChannel(points, key, label, unit))
      .filter(Boolean),
    sections: (report.coachingSections || []).map(section => ({
      number: section.id,
      label: section.name,
      comparisons: comparisonLaps.map(lapNumber => ({
        lapNumber,
        deltaSeconds: Number(section[`lap${lapNumber}Comparison`]?.timeDeltaS),
      })).filter(comparison => Number.isFinite(comparison.deltaSeconds)),
    })),
    corners: (report.coachingSections || []).map(section => ({
      number: section.id,
      label: section.name,
      startM: section.startM,
      endM: section.endM,
    })),
    sampleCount: points.length,
  }
}

const normalizeAlfanoSampleAnalytics = report => {
  const points = report.trace || []
  const sectorCount = Math.max(0, ...(report.laps || []).map(lap => lap.sectorsMs?.length || 0))
  return {
    source: 'alfano',
    referenceLap: report.overview?.fastestLapNumber,
    comparisonLaps: (report.laps || []).map(lap => lap.lapNumber).filter(number => number !== report.overview?.fastestLapNumber),
    laps: (report.laps || []).map(lap => ({
      number: lap.lapNumber,
      timeSeconds: Number(lap.lapTimeMs) / 1000,
      label: `${(Number(lap.lapTimeMs) / 1000).toFixed(3)} s`,
      status: 'Timed',
      sectors: (lap.sectorsMs || []).map(value => Number(value) / 1000),
    })),
    channels: [
      ['speed', 'Speed', 'km/h'],
      ['rpm', 'Engine speed', 'rpm'],
      ['gear', 'Gear', ''],
    ].map(([key, label, unit]) => summarizeChannel(points, key, label, unit)).filter(Boolean),
    sections: Array.from({ length: sectorCount }, (_, index) => {
      const candidates = (report.laps || []).map(lap => ({ lapNumber: lap.lapNumber, value: Number(lap.sectorsMs?.[index]) / 1000 })).filter(candidate => Number.isFinite(candidate.value))
      const best = candidates.length ? Math.min(...candidates.map(candidate => candidate.value)) : null
      return {
        number: index + 1,
        label: `Partial ${index + 1}`,
        comparisons: candidates.map(candidate => ({ lapNumber: candidate.lapNumber, deltaSeconds: candidate.value - best })),
      }
    }),
    corners: (report.corners || []).map(corner => ({
      number: corner.number,
      label: corner.name,
      baselineSpeed: Number(corner.baselineApexSpeed),
      bestSpeed: Number(corner.bestApexSpeed),
    })),
    sampleCount: points.length,
  }
}

module.exports = {
  normalizeAlfanoSampleAnalytics,
  normalizeIRacingSampleAnalytics,
}
